<template>
  <DialogPopup
    class="theme__popup-dialog"
    :style="
      avoidLeftOffset
        ? 'position: absolute; width: 40vw; max-width: 40vw; left: 0;'
        : 'position: absolute; width: 40vw; max-width: 40vw'
    "
    header-tag="h4"
    :show="localShowInfo"
    @close="closeGuide"
  >
    <template #header>
      <div
        style="
          display: flex;
          top: 0;
          flex-direction: row;
          text-align: left;
          font-size: 1rem;
          text-transform: none;
        "
      >
        Learn more about AI-powered text analysis
      </div>
    </template>
    <template #body>
      <div>
        <div v-if="infoPagination.page == 1">
          <p>
            <VisibleText
              >We configure leading open-source tools for you so you can get up and running with
              smart analysis. When you upload text data, we can pre-process it to enable even more
              powerful analysis.
            </VisibleText>
          </p>

          <p>
            <VisibleText><strong>We:</strong></VisibleText>
          </p>
          <ul>
            <li>Find and store sentences to provide context for later AI</li>
            <li>Apply sentiments to sentences</li>
            <li>Lemmatize words to support smart matching</li>
          </ul>
        </div>
        <div v-if="infoPagination.page == 2">
          <p>
            <VisibleText
              ><strong>We find and store sentences to provide context for later AI</strong>
            </VisibleText>
          </p>

          <p>
            <VisibleText></VisibleText>
          </p>
          <ul>
            <li>
              <VisibleText>
                We use a BERT and python-based package called SentenceTransformers that has been
                trained to detect sentences from text.
              </VisibleText>
            </li>
            <li>
              <VisibleText>
                We then vectorize these data, that is, convert these data into a format that
                supports mathematical similarity analysis.
                <a target="_blank" href="https://www.sbert.net/"
                  >Learn more<SvgIconDecorative icon="externalLink"
                /></a>
              </VisibleText>
            </li>
            <li>
              <VisibleText> We use this to </VisibleText>
              <ol>
                <li>
                  <VisibleText> Find similar sentences to ones you've already tagged </VisibleText>
                </li>
                <li>
                  <VisibleText>
                    Enable more advanced topic analysis, such as BerTopic-based methods and GenAI
                  </VisibleText>
                </li>
              </ol>
            </li>
          </ul>
        </div>
        <div v-if="infoPagination.page == 3">
          <p>
            <VisibleText>
              <strong>We apply sentiments to sentences</strong>
            </VisibleText>
          </p>
          <p>
            <VisibleText
              >Sentiment analysis is a process to identify emotional states from text. We use Bert
              and
              <a
                target="_blank"
                href="https://huggingface.co/cardiffnlp/twitter-roberta-base-sentiment-latest"
                >cardiffnlp/twitter-roberta-base-sentiment-latest<SvgIconDecorative
                  icon="externalLink"
              /></a>
            </VisibleText>
          </p>
          <p>
            <VisibleText
              >A response like "The staff kinda left me feeling abandoned" will be analyzed as an
              entire statement. This helps identify sarcasm or double negatives.</VisibleText
            >
          </p>
          <p style="margin-left: 2rem">
            <VisibleText>Example: "The staff left me feeling abandoned"</VisibleText>
            <span> has a sentiment of:<strong> -0.41</strong> </span>
          </p>

          <ul>
            <li>The pre-trained emotional state of 'feel' is: 0.128 (in the positive direction)</li>
            <li>
              The pre-trained emotional state of 'abandon' is: -0.4588 (in the negative direction)
            </li>
            <li>
              <a target="_blank" href="https://en.wikipedia.org/wiki/Sentiment_analysis"
                >Learn more<SvgIconDecorative icon="externalLink"
              /></a>
            </li>
          </ul>
        </div>
        <div v-if="infoPagination.page == 4">
          <p>
            <VisibleText>
              <strong>We lemmatize words to support smart matching</strong>
            </VisibleText>
          </p>
          <p>
            <VisibleText
              >To make the percentage of matched topics flexible and 'smart', we find additional
              information from each response, called lemmas.
            </VisibleText>
          </p>

          <p>
            <VisibleText
              ><strong>Lemmatization:</strong> Group together inflected forms of words (like across
              tenses, gender, etc). For example, 'feeling' or 'feelings' or 'felt' all share the
              lemma: 'feel'</VisibleText
            >
          </p>
          <ul>
            <li>We use a Spacy package called en_core_web_md</li>
            <li>
              Warning: These are trained by humans and are incomplete. Not all lemmas that you
              expect to find will be found.
            </li>
            <li>
              <a target="_blank" href="https://en.wikipedia.org/wiki/Lemmatisation"
                >Learn more<SvgIconDecorative icon="externalLink"
              /></a>
            </li>
          </ul>
        </div>

        <div class="theme__popup-pagination" v-show="infoPagination.totalPages > 1">
          <span class="details">
            {{ translate('Page', $options.name) }}
            {{ infoPagination.page }}
            {{ translate('of', $options.name) }}
            {{ infoPagination.totalPages }}
          </span>
          <span class="actions">
            <button
              type="button"
              class="pagination-btn"
              :disabled="infoPagination.page === 1"
              @click="onClickPrevPageLemmas"
            >
              <VisibleText> Prev </VisibleText>
            </button>
            |
            <button
              type="button"
              class="pagination-btn"
              :disabled="infoPagination.page === infoPagination.totalPages"
              @click="onClickNextPageLemmas"
            >
              <VisibleText> Next </VisibleText>
            </button>
          </span>
        </div>
      </div>
    </template>
  </DialogPopup>
</template>

<script>
import DialogPopup from '@/components/UI/DialogPopup.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIconDecorative.vue';

export default {
  name: 'TextAIMiniGuide',
  components: {
    DialogPopup,
    SvgIconDecorative,
  },
  props: {
    showInfo: {
      type: Boolean,
      default: () => false,
    },
    avoidLeftOffset: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      localShowInfo: false,
      infoPagination: {
        page: 1,
        limit: 2, //limit of how many are shown in a page
        totalPages: 4,
        startIndx: 0,
        endIndx: 0,
      },
    };
  },
  methods: {
    onClickNextPageLemmas() {
      if (this.infoPagination.page === this.infoPagination.totalPages) return;
      this.infoPagination.page = this.infoPagination.page + 1;
      this.setPaginationDetailsLemmas(
        this.infoPagination.startIndx + this.infoPagination.limit,
        this.infoPagination.endIndx + this.infoPagination.limit,
      );
    },
    onClickPrevPageLemmas() {
      if (this.infoPagination.page === 1) return;
      this.infoPagination.page = this.infoPagination.page - 1;
      this.setPaginationDetailsLemmas(
        this.infoPagination.startIndx - this.infoPagination.limit,
        this.infoPagination.endIndx - this.infoPagination.limit,
      );
    },
    setPaginationDetailsLemmas(start, end) {
      this.infoPagination.startIndx = start;
      this.infoPagination.endIndx = end;
    },
    closeGuide() {
      this.$emit('closingGuide');
      this.localShowInfo = false;
    },
  },
  created() {
    if (this.showInfo) {
      this.localShowInfo = true;
    }
  },
  watch: {
    showInfo(newVal) {
      if (newVal) {
        this.localShowInfo = true;
      } else {
        this.localShowInfo = false;
      }
    },
  },
};
</script>
